import {
  createBrowserRouter,
  RouterProvider,
  redirect,
} from "react-router-dom";
import { RootLayout } from "@/layouts/RootLayout";
import { AppLayout } from "@/AppLayout";
import { StoreGuard } from "@/guards/StoreGuard";
import { rootLoader } from "@/loaders/rootLoader";

// Auth routes
import AuthLayout from "./auth/layout";
import LoginPage from "./auth/login";
import VerifyOtpPage from "./auth/verify-otp";
import StoreSelectorPage from "./auth/store-selector";
import NewStorePage from "./auth/new-store/index";
import NewStoreSellerPage from "./auth/new-store/seller";
import NewStoreWholesalerPage from "./auth/new-store/wholesaler";

// Store routes
import StoreLayout from "./store/layout";
import WebBuilderLayout from "./store/webbuilder/layout";
import WebBuilderIndex from "./store/webbuilder/index";
import WebBuilderMainLayout from "./store/webbuilder/main/layout";
import WebBuilderFullscreenLayout from "./store/webbuilder/fullscreen/layout";
import PreviewPage from "./store/webbuilder/fullscreen/preview";
import OnboardingPage from "./store/webbuilder/main/onboarding";
import OnboardingTemplatesPage from "./store/webbuilder/main/onboarding-templates";
import TemplatesSelectPage from "./store/webbuilder/main/templates-select";
import ImportPage from "./store/webbuilder/main/import";
import NewPagePage from "./store/webbuilder/main/new-page";
import EditPage from "./store/webbuilder/fullscreen/edit";
import DashboardPage from "./store/dashboard";
import CustomersIndexPage from "./store/customers";
import CustomerDetailsPage from "./store/customers/[customerId]";
import CustomerCreatePage from "./store/customers/create";
import CustomerEditPage from "./store/customers/[customerId]/edit";
import ProductsIndexPage from "./store/product";
import CollectionPage from "./store/product/collection";
import CategoryCreatePage from "./store/product/collection/create";
import ProductsCreatePage from "./store/product/create";
import WholesalerPage from "./store/product/wholesaler";
import WholesalerDetailPage from "./store/product/wholesaler/[slug]";
import InventoryPage from "./store/product/inventory";
import OrdersIndexPage from "./store/orders";
import OrderSetsIndexPage from "./store/order-sets";
import WholesaleOrderSetDetailsPage from "./store/order-sets/[orderSetId]";
import OrderDetailsPage from "./store/orders/[orderId]";
import OrderSetDetailsPage from "./store/orders/[orderId]/order-sets/[orderSetId]";
import SettingsIndexPage from "./store/settings";
import ConfigurationPage from "./store/settings/configuration";
import LocationsPage from "./store/settings/locations";
import SubscriptionPage from "./store/settings/subscription";
import SellersIndexPage from "./store/sellers";
import SellerDetailsPage from "./store/sellers/[sellerId]";
import WalletPage from "./store/wallet";
import TicketsIndexPage from "./store/tickets";
import TicketCreatePage from "./store/tickets/create";
import TicketDetailPage from "./store/tickets/[ticketId]";
import CouponsIndexPage from "./store/coupons";
import AmountOffOrderPage from "./store/coupons/amount-off-order";
import AmountOffProductsPage from "./store/coupons/amount-off-products";
import BuyXGetYPage from "./store/coupons/buy-x-get-y";
import FreeShippingPage from "./store/coupons/free-shipping";
import MarketingPage from "./store/marketing";
import CampaignIndexPage from "./store/marketing/campaign";
import CampaignCreatePage from "./store/marketing/campaign/create";
import AutomationIndexPage from "./store/marketing/automation";
import AutomationCreatePage from "./store/marketing/automation/create";
import AutomationEditPage from "./store/marketing/automation/[automationId]/edit";
import TemplatesIndexPage from "./store/marketing/templates";
import AbandonedCartPage from "./store/marketing/abandoned-cart";
import InvitePage from "./invite";
import OnboardingIndexPage from "./onboarding";
import OnboardingSellerPage from "./onboarding/seller";
import OnboardingWholesalerPage from "./onboarding/wholesaler";
import FileManagerPage from "./store/files/manage";

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AppLayout>
        <RootLayout />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: rootLoader,
        element: <div>Loading...</div>,
      },
      {
        path: "auth",
        element: <AuthLayout />,
        children: [
          { path: "login", element: <LoginPage /> },
          { path: "verify-otp", element: <VerifyOtpPage /> },
          {
            path: "store-selector",
            element: <StoreSelectorPage />,
          },
          {
            path: "new-store",
            element: <NewStorePage />,
          },
          {
            path: "new-store/seller",
            element: <NewStoreSellerPage />,
          },
          {
            path: "new-store/wholesaler",
            element: <NewStoreWholesalerPage />,
          },
        ],
      },
      // Legacy redirects
      { path: "login", loader: () => redirect("/auth/login") },
      { path: "verify-otp", loader: () => redirect("/auth/verify-otp") },
      {
        path: "store-selector",
        loader: () => redirect("/auth/store-selector"),
      },

      // Public routes
      { path: "invite", element: <InvitePage /> },
      { path: "onboarding", element: <OnboardingIndexPage /> },
      { path: "onboarding/seller", element: <OnboardingSellerPage /> },
      { path: "onboarding/wholesaler", element: <OnboardingWholesalerPage /> },

      // Webbuilder preview intentionally bypasses StoreLayout and webbuilder
      // chrome so iframe rendering gets a blank document surface.
      {
        path: ":storeType/:storeId/webbuilder/preview",
        element: (
          <StoreGuard>
            <PreviewPage />
          </StoreGuard>
        ),
      },

      // Store routes
      {
        path: ":storeType/:storeId",
        element: (
          <StoreGuard>
            <StoreLayout />
          </StoreGuard>
        ),
        children: [
          { path: "dashboard", element: <DashboardPage /> },
          { path: "product", element: <ProductsIndexPage /> },
          { path: "product/create", element: <ProductsCreatePage /> },
          { path: "product/collection", element: <CollectionPage /> },
          {
            path: "product/collection/create",
            element: <CategoryCreatePage />,
          },
          { path: "product/inventory", element: <InventoryPage /> },
          { path: "files/manage", element: <FileManagerPage /> },
          { path: "product/wholesaler", element: <WholesalerPage /> },
          {
            path: "product/wholesaler/:slug",
            element: <WholesalerDetailPage />,
          },
          { path: "customers", element: <CustomersIndexPage /> },
          { path: "customers/create", element: <CustomerCreatePage /> },
          { path: "customers/:customerId", element: <CustomerDetailsPage /> },
          { path: "customers/:customerId/edit", element: <CustomerEditPage /> },
          { path: "orders", element: <OrdersIndexPage /> },
          { path: "order-sets", element: <OrderSetsIndexPage /> },
          {
            path: "order-sets/:orderSetId",
            element: <WholesaleOrderSetDetailsPage />,
          },
          { path: "orders/:orderId", element: <OrderDetailsPage /> },
          {
            path: "orders/:orderId/order-sets/:orderSetId",
            element: <OrderSetDetailsPage />,
          },
          { path: "settings", element: <SettingsIndexPage /> },
          { path: "settings/configuration", element: <ConfigurationPage /> },
          { path: "settings/locations", element: <LocationsPage /> },
          { path: "settings/subscription", element: <SubscriptionPage /> },
          { path: "sellers", element: <SellersIndexPage /> },
          { path: "sellers/:sellerId", element: <SellerDetailsPage /> },
          { path: "wallet", element: <WalletPage /> },
          { path: "tickets", element: <TicketsIndexPage /> },
          { path: "tickets/create", element: <TicketCreatePage /> },
          { path: "tickets/:ticketId", element: <TicketDetailPage /> },
          { path: "coupons", element: <CouponsIndexPage /> },
          { path: "coupons/amount-off-order", element: <AmountOffOrderPage /> },
          {
            path: "coupons/amount-off-products",
            element: <AmountOffProductsPage />,
          },
          { path: "coupons/buy-x-get-y", element: <BuyXGetYPage /> },
          { path: "coupons/free-shipping", element: <FreeShippingPage /> },
          { path: "marketing", element: <MarketingPage /> },
          { path: "marketing/campaign", element: <CampaignIndexPage /> },
          {
            path: "marketing/campaign/create",
            element: <CampaignCreatePage />,
          },
          { path: "marketing/automation", element: <AutomationIndexPage /> },
          {
            path: "marketing/automation/create",
            element: <AutomationCreatePage />,
          },
          {
            path: "marketing/automation/:automationId/edit",
            element: <AutomationEditPage />,
          },
          { path: "marketing/templates", element: <TemplatesIndexPage /> },
          { path: "marketing/abandoned-cart", element: <AbandonedCartPage /> },
          // Allow middleware to redirect to /:storeType/:storeId/onboarding
          // by reusing the existing WebBuilderMainLayout + OnboardingPage
          {
            path: "onboarding",
            element: <WebBuilderMainLayout />,
            children: [{ index: true, element: <OnboardingPage /> }],
          },
          // TODO: replace placeholder above with actual DashboardPage import
          {
            path: "webbuilder",
            element: <WebBuilderLayout />,
            children: [
              { index: true, element: <WebBuilderIndex /> },
              {
                path: "onboarding",
                element: <WebBuilderMainLayout />,
                children: [
                  { index: true, element: <OnboardingPage /> },
                  { path: "templates", element: <OnboardingTemplatesPage /> },
                  {
                    path: "templates/select",
                    element: <WebBuilderMainLayout />,
                    children: [
                      { index: true, element: <TemplatesSelectPage /> },
                    ],
                  },
                  {
                    path: "import",
                    element: <WebBuilderMainLayout />,
                    children: [{ index: true, element: <ImportPage /> }],
                  },
                  {
                    path: "edit",
                    element: <WebBuilderFullscreenLayout />,
                    children: [{ index: true, element: <EditPage /> }],
                  },
                ],
              },
              {
                path: "templates/select",
                element: <WebBuilderMainLayout />,
                children: [{ index: true, element: <TemplatesSelectPage /> }],
              },
              {
                path: "import",
                element: <WebBuilderMainLayout />,
                children: [{ index: true, element: <ImportPage /> }],
              },
              {
                path: "new-page",
                element: <WebBuilderMainLayout />,
                children: [{ index: true, element: <NewPagePage /> }],
              },
              {
                path: "edit",
                element: <WebBuilderFullscreenLayout />,
                children: [{ index: true, element: <EditPage /> }],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
